import React from 'react';
import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GenericForm from './GenericForm';

interface GenericModalProps {
    visible: boolean;
    onDismiss: () => void;
    onSubmit: (values: any) => void;
    initialValues: any;
    fieldGroups: any[];
    title: string;
    description?: string;
    maxWidth?: number;
    /** When true, clicking outside or pressing Escape will NOT close the dialog */
    disableBackdropClose?: boolean;
    /** When true, shows a small persistent X button in the upper right corner */
    showCloseIcon?: boolean;
    /** Remounts the inner form when this value changes (e.g. selected record id). */
    formKey?: React.Key;
    /** When true, Formik resets when initialValues change (e.g. after async load). */
    enableReinitialize?: boolean;
}

const GenericFormModal: React.FC<GenericModalProps> = ({
    visible,
    onDismiss,
    onSubmit,
    initialValues,
    fieldGroups,
    title,
    description,
    maxWidth = 600, // Adjust maxWidth as needed
    disableBackdropClose = false,
    showCloseIcon = false,
    formKey,
    enableReinitialize = false,
}) => {

    const handleClose = (
        _event: object,
        reason: 'backdropClick' | 'escapeKeyDown'
    ) => {
        if (disableBackdropClose && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
            return;
        }
        onDismiss();
    };

    return (
        <Dialog 
            open={visible} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth 
            disableEscapeKeyDown={disableBackdropClose}
            slotProps={{
                paper: {
                    sx: {
                        maxWidth,
                        borderRadius: '8px',
                        p: 2,
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pr: showCloseIcon ? 1 : 2,
                }}
            >
                <Typography variant="h6" component="div">
                    {title}
                </Typography>
                {showCloseIcon && (
                    <IconButton
                        aria-label="close"
                        size="small"
                        onClick={onDismiss}
                        sx={{ ml: 1 }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {description ? <Typography variant="body1">{description}</Typography> : null}
                    <GenericForm
                        key={formKey}
                        onSubmit={onSubmit}
                        onCancel={onDismiss}
                        initialValues={initialValues}
                        groups={fieldGroups}
                        enableReinitialize={enableReinitialize}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default GenericFormModal;